import LoadingStatus from 'enums/loadingStatus';
import fastdom from 'fastdom';
import { distinctUntilChanged, filter, fromEvent, map } from 'rxjs';
import { store$ } from '@redux/store';
import { selectSelectedSlot } from '../redux/selectors';
import slotButton from './timeSlot';
import ModalStep from '../enums/modalStep';

export default class SlotCarousel {
  slotClickSubscription = null;

  constructor(rootComponent, carousel, selectHandler) {
    this.rootComponent = rootComponent;
    this.id = 'self-guided-tour-slots-carousel';
    this.baseCarousel = carousel;
    this.selectHandler = selectHandler;

    this.selectedSlot$ = store$.pipe(
      map(selectSelectedSlot),
      distinctUntilChanged(),
      filter(Boolean)
    );

    // these subscriptions are long-lived, we keep them around as long as this object is alive
    this.selectedSlot$.subscribe(SlotCarousel.handleSelectedSlotChange);
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
  }

  static getContainer() {
    return document.querySelector('[data-slot-buttons]');
  }

  static getSlotElement(slotDate) {
    return document.querySelector(`.nhs-c-datepicker__button[data-slot="${slotDate}"]`);
  }

  static getSelectedSlot() {
    return document.querySelector('.nhs-c-datepicker__button--selected');
  }

  static getSkeleton() {
    let count = 0;
    let skeletonHtml = '';

    while (count < 12) {
      skeletonHtml += `
      <li>
        <button type="button" class="nhs-c-datepicker__button nhs-c-card-skeleton__item--animated">
          <span class="nhs-o-typography--sans-body-1-medium-medium">&nbsp;</span>
        </button>
      </li>`;
      count += 1;
    }
    return skeletonHtml;
  }

  static getErrorMessage() {
    return `<li>
        <button type="button" class="nhs-c-datepicker__button">
          <div class="nhs-c-datepicker__time nhs-u-flex-column">
            <span class="nhs-o-typography--sans-label-1-xxx-small-medium-uppercase">No Available Times</span>
          </div>
        </button>
      </li>`;
  }

  static handleSelectedSlotChange(slot) {
    // if the slot is null, we don't need to do anything
    if (!slot) {
      return;
    }
    fastdom.mutate(() => {
      const selectedSlot = SlotCarousel.getSelectedSlot();
      const slotElement = SlotCarousel.getSlotElement(slot.Date);

      if (slotElement === selectedSlot) {
        // user clicked the same slot
        return;
      }

      if (selectedSlot) {
        selectedSlot.classList.remove('nhs-c-datepicker__button--selected');
      }

      if (slotElement) {
        slotElement.classList.add('nhs-c-datepicker__button--selected');
      }
    });
  }

  onDomUpdate(event) {
    if (event.detail && event.detail.step === ModalStep.DateTimeSelection) {
      const container = SlotCarousel.getContainer();
      if (!container) { return; }
      this.baseCarousel.setSettings({
        itemListQuerySelector: '[data-slot-buttons]',
        previousQuerySelector: '[data-nav-left]',
        nextQuerySelector: '[data-nav-right]',
      });
      this.baseCarousel.create();
      this.slotClickSubscription = fromEvent(container, 'click').subscribe(this.handleSlotClick.bind(this));
    } else if (this.slotClickSubscription) {
      this.baseCarousel.destroy();
      this.slotClickSubscription.unsubscribe();
      this.slotClickSubscription = null;
    }
  }

  handleSlotClick(event) {
    const button = event.target.closest('button');
    if (!button) { return; }
    const slot = button.getAttribute('data-slot');
    this.selectHandler(slot);
  }

  update(fetchingStatus, tourDateSlots) {
    const slotsCarouselContainer = SlotCarousel.getContainer();

    if (!slotsCarouselContainer) {
      return;
    }

    switch (fetchingStatus) {
      case LoadingStatus.Loading:
        slotsCarouselContainer.innerHTML = SlotCarousel.getSkeleton();
        break;
      case LoadingStatus.Failed:
        slotsCarouselContainer.innerHTML = SlotCarousel.getErrorMessage();
        break;
      default:
        fastdom.mutate(() => {
          slotsCarouselContainer.innerHTML = this.getCarousel(tourDateSlots);
          this.baseCarousel.refresh();
        });
        break;
    }
  }

  getCarousel(tourDateSlots = []) {
    return tourDateSlots.map(slot => slotButton(slot, this.handleSlotClick.bind(this))).join('');
  }

  render() {
    return `
    <div id="${this.id}" data-slots-carousel-container>
      <div class="nhs-c-datepicker__container" data-slot-container>
        <button type="button" class="nhs-c-datepicker__nav-btn nhs-c-datepicker__nav-left" id="scrollLeft" data-nav-left data-arrow-left data-move-arrow="-79">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="20" viewBox="0 0 10 20" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M0.146447 19.3536C-0.0488156 19.1583 -0.0488156 18.8417 0.146447 18.6464L8.43934 10.3536C8.6346 10.1583 8.6346 9.84171 8.43934 9.64645L0.146446 1.35355C-0.0488163 1.15829 -0.0488164 0.841709 0.146446 0.646446C0.341708 0.451185 0.65829 0.451185 0.853553 0.646446L9.14645 8.93934C9.73223 9.52513 9.73223 10.4749 9.14645 11.0607L0.853553 19.3536C0.658291 19.5488 0.341709 19.5488 0.146447 19.3536Z" fill="#1E1D21"/>
          </svg>
        </button>
        <ul class="nhs-c-datepicker__buttons" data-slot-buttons>${SlotCarousel.getSkeleton()}</ul>
        <button type="button" class="nhs-c-datepicker__nav-btn nhs-c-datepicker__nav-right" id="scrollRight" data-nav-right data-arrow-right data-move-arrow="79">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="20" viewBox="0 0 10 20" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M0.146447 19.3536C-0.0488156 19.1583 -0.0488156 18.8417 0.146447 18.6464L8.43934 10.3536C8.6346 10.1583 8.6346 9.84171 8.43934 9.64645L0.146446 1.35355C-0.0488163 1.15829 -0.0488164 0.841709 0.146446 0.646446C0.341708 0.451185 0.65829 0.451185 0.853553 0.646446L9.14645 8.93934C9.73223 9.52513 9.73223 10.4749 9.14645 11.0607L0.853553 19.3536C0.658291 19.5488 0.341709 19.5488 0.146447 19.3536Z" fill="#1E1D21"/>
          </svg>
        </button>
      </div>
    </div>
    `;
  }
}
